/**
 * NFT Rarity Calculator Utility
 * Helps calculate and understand NFT rarity percentages
 */

export interface TraitRarity {
  trait_type: string;
  value: string;
  count: number;
  rarity: number; // Percentage (0-100)
}

export interface CollectionStats {
  totalSupply: number;
  traits: Record<string, Record<string, number>>; // trait_type -> value -> count
}

/**
 * Calculate rarity percentage for a specific trait value
 */
export function calculateTraitRarity(
  traitType: string,
  value: string,
  collectionStats: CollectionStats
): number {
  const traitCounts = collectionStats.traits[traitType];
  if (!traitCounts || !traitCounts[value]) {
    return 0;
  }
  
  const count = traitCounts[value];
  const rarity = (count / collectionStats.totalSupply) * 100;
  return Math.round(rarity * 100) / 100; // Round to 2 decimal places
}

/**
 * Calculate overall NFT rarity score based on all traits
 */
export function calculateNFTRarityScore(
  attributes: Array<{ trait_type: string; value: string }>,
  collectionStats: CollectionStats
): number {
  if (attributes.length === 0) return 0;
  
  let totalRarityScore = 0;
  let validTraits = 0;
  
  for (const attr of attributes) {
    const rarity = calculateTraitRarity(attr.trait_type, attr.value, collectionStats);
    if (rarity > 0) {
      totalRarityScore += (1 / rarity) * 100; // Inverse rarity for scoring
      validTraits++;
    }
  }
  
  return validTraits > 0 ? totalRarityScore / validTraits : 0;
}

/**
 * Get rarity rank (1 = most rare)
 */
export function getRarityRank(
  rarityScore: number,
  allRarityScores: number[]
): number {
  const sortedScores = [...allRarityScores].sort((a, b) => b - a);
  return sortedScores.indexOf(rarityScore) + 1;
}

/**
 * Common rarity tiers for easy categorization
 */
export const RARITY_TIERS = {
  LEGENDARY: { min: 0, max: 1, label: 'Legendary', color: '#FFD700' },
  EPIC: { min: 1, max: 5, label: 'Epic', color: '#9C27B0' },
  RARE: { min: 5, max: 15, label: 'Rare', color: '#2196F3' },
  UNCOMMON: { min: 15, max: 40, label: 'Uncommon', color: '#4CAF50' },
  COMMON: { min: 40, max: 100, label: 'Common', color: '#757575' }
} as const;

/**
 * Get rarity tier for a given percentage
 */
export function getRarityTier(rarityPercentage: number) {
  for (const [key, tier] of Object.entries(RARITY_TIERS)) {
    if (rarityPercentage >= tier.min && rarityPercentage <= tier.max) {
      return { key, ...tier };
    }
  }
  return { key: 'COMMON', ...RARITY_TIERS.COMMON };
}

/**
 * Generate suggested rarity percentages for balanced collection
 */
export function generateBalancedRarities(totalSupply: number) {
  return {
    legendary: Math.max(1, Math.floor(totalSupply * 0.01)), // 1%
    epic: Math.max(1, Math.floor(totalSupply * 0.05)), // 5%
    rare: Math.max(1, Math.floor(totalSupply * 0.15)), // 15%
    uncommon: Math.max(1, Math.floor(totalSupply * 0.30)), // 30%
    common: Math.floor(totalSupply * 0.49) // 49%
  };
}

/**
 * Validate rarity distribution for a collection
 */
export function validateRarityDistribution(
  traits: Array<{ value: string; count: number }>,
  totalSupply: number
): { isValid: boolean; issues: string[] } {
  const issues: string[] = [];
  let totalCount = 0;
  
  for (const trait of traits) {
    totalCount += trait.count;
    
    if (trait.count <= 0) {
      issues.push(`Trait "${trait.value}" has invalid count: ${trait.count}`);
    }
    
    if (trait.count > totalSupply) {
      issues.push(`Trait "${trait.value}" count (${trait.count}) exceeds total supply (${totalSupply})`);
    }
  }
  
  if (totalCount !== totalSupply) {
    issues.push(`Total trait counts (${totalCount}) don't match collection supply (${totalSupply})`);
  }
  
  return {
    isValid: issues.length === 0,
    issues
  };
}

/**
 * Example usage and preset trait suggestions
 */
export const COMMON_TRAIT_EXAMPLES = {
  background: ['Blue', 'Red', 'Green', 'Purple', 'Gold', 'Rainbow'],
  eyes: ['Normal', 'Laser', 'Glowing', 'Closed', 'Winking', 'Robotic'],
  mouth: ['Smile', 'Frown', 'Open', 'Tongue', 'Pipe', 'Mask'],
  accessories: ['None', 'Hat', 'Glasses', 'Earrings', 'Necklace', 'Crown'],
  clothing: ['T-Shirt', 'Hoodie', 'Suit', 'Armor', 'Robe', 'Jacket'],
  special: ['Aura', 'Wings', 'Halo', 'Fire', 'Lightning', 'Cosmic']
} as const;

/**
 * Generate random attributes with balanced rarity
 */
export function generateRandomAttributes(
  traitTypes: string[],
  totalSupply: number
): Array<{ trait_type: string; values: Array<{ value: string; rarity: number }> }> {
  const rarities = generateBalancedRarities(totalSupply);
  
  return traitTypes.map(traitType => {
    const values = COMMON_TRAIT_EXAMPLES[traitType as keyof typeof COMMON_TRAIT_EXAMPLES] || 
                   ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'];
    
    return {
      trait_type: traitType,
      values: values.map((value, index) => {
        // Distribute rarity based on index (later = rarer)
        const rarityKeys = Object.keys(rarities);
        const rarityKey = rarityKeys[Math.min(index, rarityKeys.length - 1)];
        const rarityPercentage = Object.values(RARITY_TIERS).find(tier => 
          tier.label.toLowerCase() === rarityKey
        )?.max || 50;
        
        return {
          value,
          rarity: rarityPercentage
        };
      })
    };
  });
}
